import { useEffect, useState } from 'react';
import { useAuth } from '@/contexts/useAuth';
import { supabase } from '@/utils/supabase';
import { createComponentLogger } from '@/utils/logger';

const log = createComponentLogger('usePickupForceRepricePermission', 'pickup');
const FORCE_REPRICE_PERMISSION = 'force_pickup_reprice';

export function usePickupForceRepricePermission(): {
  canForceReprice: boolean;
  isLoadingForceRepricePermission: boolean;
} {
  const { user } = useAuth();
  const [canForceReprice, setCanForceReprice] = useState(false);
  const [loading, setLoading] = useState(Boolean(user?.id));

  useEffect(() => {
    let cancelled = false;
    if (!user?.id) {
      setCanForceReprice(false);
      setLoading(false);
      return undefined;
    }

    setCanForceReprice(false);
    setLoading(true);
    const loadPermission = async (): Promise<void> => {
      try {
        const { data, error } = await supabase.rpc('get_user_permissions', { user_id: user.id });
        if (cancelled) {
          return;
        }
        if (error) {
          log.error('Failed to load force-reprice permission', { userId: user.id }, error);
          setCanForceReprice(false);
          return;
        }
        setCanForceReprice((data || []).some(
          (row: { permission_id: string }) => row.permission_id === FORCE_REPRICE_PERMISSION
        ));
      } catch (error: unknown) {
        if (!cancelled) {
          log.error(
            'Failed to load force-reprice permission',
            { userId: user.id },
            error instanceof Error ? error : undefined
          );
          setCanForceReprice(false);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    void loadPermission();

    return () => {
      cancelled = true;
    };
  }, [user?.id]);

  return {
    canForceReprice,
    isLoadingForceRepricePermission: loading
  };
}
